/**
 * Convert Firestore Timestamp / Date values into serializable millis for Redux.
 * Walks nested objects and arrays from Firestore document snapshots.
 */

const isPlainTimestampShape = (value) => {
  if (value == null || typeof value !== "object" || Array.isArray(value)) {
    return false;
  }

  if (typeof value.seconds !== "number" || !Number.isFinite(value.seconds)) {
    return false;
  }

  const keys = Object.keys(value);
  if (keys.length < 1 || keys.length > 2) {
    return false;
  }

  return keys.every((key) => key === "seconds" || key === "nanoseconds");
};

export const normalizeFirestoreDateValue = (value) => {
  if (value == null) {
    return null;
  }

  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }

  if (typeof value === "string") {
    const parsedValue = Date.parse(value);
    return Number.isNaN(parsedValue) ? null : parsedValue;
  }

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value.getTime();
  }

  if (typeof value.toMillis === "function") {
    return value.toMillis();
  }

  if (isPlainTimestampShape(value)) {
    const nanoseconds = typeof value.nanoseconds === "number" ? value.nanoseconds : 0;
    return value.seconds * 1000 + Math.floor(nanoseconds / 1000000);
  }

  return null;
};

export const isFirestoreTimestampLike = (value) => {
  if (value == null || typeof value !== "object") {
    return false;
  }

  if (value instanceof Date) {
    return true;
  }

  if (typeof value.toMillis === "function") {
    return true;
  }

  return isPlainTimestampShape(value);
};

export const sanitizeFirestoreDataForRedux = (value) => {
  if (value == null || typeof value !== "object") {
    return value;
  }

  if (isFirestoreTimestampLike(value)) {
    return normalizeFirestoreDateValue(value);
  }

  if (Array.isArray(value)) {
    return value.map((item) => sanitizeFirestoreDataForRedux(item));
  }

  const next = {};
  Object.keys(value).forEach((key) => {
    next[key] = sanitizeFirestoreDataForRedux(value[key]);
  });
  return next;
};
